import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from './Icon';

const stack = [
  { icon: 'chip', label: 'Hardware', detail: 'PCB · firmware · sensors' },
  { icon: 'signal', label: 'Connectivity', detail: 'BLE · Wi-Fi · LoRa · LTE' },
  { icon: 'code', label: 'Cloud & Apps', detail: 'APIs · dashboards · mobile' },
  { icon: 'spark', label: 'Intelligence', detail: 'ML · LLMs · edge AI' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

// A stylised view of the full product stack, from device to AI, with a data pulse travelling through it.
const StackDiagram = () => (
  <div className="relative mx-auto w-full max-w-md">
    <div className="absolute bottom-8 left-[2.35rem] top-8 w-px bg-gradient-to-b from-signal-500/60 via-ink-600 to-signal-500/60" />
    <motion.div
      className="absolute left-[2.1rem] h-2.5 w-2.5 rounded-full bg-signal-400 shadow-[0_0_16px_4px_rgba(78,232,168,0.5)]"
      initial={{ top: '2rem' }}
      animate={{ top: ['2rem', 'calc(100% - 2.5rem)'] }}
      transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut', repeatType: 'reverse' }}
    />
    <ul className="relative space-y-4">
      {stack.map((layer, i) => (
        <motion.li
          key={layer.label}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={i + 2}
          className="card flex items-center gap-4 p-4 backdrop-blur"
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-signal-500/30 bg-ink-800 text-signal-400">
            <Icon name={layer.icon} className="h-5 w-5" />
          </span>
          <span className="flex-1">
            <span className="block font-display font-semibold text-white">{layer.label}</span>
            <span className="block font-mono text-xs text-slate-500">{layer.detail}</span>
          </span>
          <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
        </motion.li>
      ))}
    </ul>
  </div>
);

const Hero = () => (
  <section className="relative overflow-hidden">
    <div className="grid-bg pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" />
    <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-[48rem] -translate-x-1/2 rounded-full bg-signal-500/10 blur-3xl" />

    <div className="container-site relative grid items-center gap-16 pb-24 pt-36 md:pt-44 lg:grid-cols-[1.15fr_1fr] lg:pb-32">
      <div>
        <motion.p variants={fadeUp} initial="hidden" animate="show" className="eyebrow">
          Electronics · IoT · AI · Software
        </motion.p>
        <motion.h1
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={1}
          className="mt-5 font-display text-4xl font-bold leading-[1.08] text-white sm:text-5xl xl:text-6xl"
        >
          From circuit board to cloud <span className="whitespace-nowrap text-signal-400">to AI.</span>
        </motion.h1>
        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={2}
          className="mt-6 max-w-xl text-lg leading-relaxed text-slate-400"
        >
          Gomagen Labs is a Moncton-based technology consultancy that designs and builds connected products: custom
          electronics, embedded firmware, IoT platforms, and practical AI for small and medium businesses, all under one roof.
        </motion.p>
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={3}
          className="mt-10 flex flex-col gap-3 sm:flex-row"
        >
          <Link to="/contact" className="btn-primary">
            Start a project <Icon name="arrow" className="h-4 w-4" />
          </Link>
          <Link to="/services" className="btn-secondary">
            Explore services
          </Link>
        </motion.div>
      </div>

      <StackDiagram />
    </div>
  </section>
);

export default Hero;
